import { useId } from 'react';
import { Box } from '@mui/material';
import { useChartColors } from '../dashboard/charts/theme';
import { NO_MOTION, SR_ONLY, clamp01, svgId } from './heroStyles';

export type GarmentKind = 'tshirt' | 'polo' | 'shirt' | 'trouser' | 'jacket' | 'hoodie';

interface Silhouette { outline: string; details?: string; label: string }

// All drawn in a 120 x 112 box. Details are stroke-only lines (collar, placket, zip, hood).
const SILHOUETTES: Record<GarmentKind, Silhouette> = {
    tshirt: {
        label: 'T-shirt',
        outline: 'M38 6 L18 14 L2 34 L18 46 L26 40 L26 106 L94 106 L94 40 L102 46 L118 34 L102 14 L82 6 Q60 22 38 6 Z',
    },
    polo: {
        label: 'Polo shirt',
        outline: 'M40 6 L18 14 L2 34 L18 46 L26 40 L26 106 L94 106 L94 40 L102 46 L118 34 L102 14 L80 6 L68 14 L60 12 L52 14 Z',
        details: 'M52 14 L60 30 L68 14 M60 30 L60 44 M40 6 L52 14 M80 6 L68 14',
    },
    shirt: {
        label: 'Shirt',
        outline: 'M40 6 L22 12 L8 40 L4 92 L16 94 L24 50 L26 106 L94 106 L96 50 L104 94 L116 92 L112 40 L98 12 L80 6 L60 20 Z',
        details: 'M60 20 L60 106 M40 6 L50 18 L60 20 L70 18 L80 6',
    },
    trouser: {
        label: 'Trousers',
        outline: 'M30 4 L90 4 L97 108 L68 108 L60 40 L52 108 L23 108 Z',
        details: 'M30 14 L90 14 M60 14 L60 40',
    },
    jacket: {
        label: 'Jacket',
        outline: 'M42 6 L22 12 L8 40 L6 96 L18 98 L26 52 L26 108 L94 108 L94 52 L102 98 L114 96 L112 40 L98 12 L78 6 L60 26 Z',
        details: 'M60 26 L60 108 M42 6 L50 30 M78 6 L70 30 M34 70 L48 70 M72 70 L86 70',
    },
    hoodie: {
        label: 'Hoodie',
        outline: 'M38 14 Q60 -6 82 14 L100 18 L114 46 L112 96 L100 98 L94 56 L94 106 L26 106 L26 56 L20 98 L8 96 L6 46 L20 18 Z',
        details: 'M44 16 Q60 34 76 16 M54 26 L54 44 M66 26 L66 44 M38 78 L82 78 L76 96 L44 96 Z',
    },
};

/** Picks a silhouette from `Style.garmentType` (free text). Unknown or empty falls back to a shirt. */
export function garmentKindFor(garmentType?: string | null): GarmentKind {
    const t = (garmentType ?? '').toLowerCase();
    if (!t.trim()) return 'shirt';
    if (/hood|sweat/.test(t)) return 'hoodie';
    if (/polo/.test(t)) return 'polo';
    if (/jacket|coat|blazer|outer|parka|vest/.test(t)) return 'jacket';
    if (/\bt-?shirt|\btee\b|tank|singlet/.test(t)) return 'tshirt';
    if (/shirt|blouse/.test(t)) return 'shirt';
    if (/trouser|pant|jean|legging|short|jogger|chino|bottom|skirt/.test(t)) return 'trouser';
    if (/\btop\b/.test(t)) return 'tshirt';
    return 'shirt';
}

export interface GarmentFillProps {
    garmentType?: string | null;
    /** Each a share 0..1 of the ordered quantity; `undefined` means "not visible to this user". */
    cut?: number;
    sewn?: number;
    finished?: number;
    /** Plain-language summary for screen readers; one is built from the shares when omitted. */
    description?: string;
    width?: number | string;
}

const pctText = (v: number) => `${Math.round(clamp01(v) * 100)}%`;

/**
 * The garment silhouette for the order's style, filled bottom-up: a light band to the cut share, a
 * middle band to the sewn share and a solid band to the finished share. With no shares given it is
 * an empty outline (never a made-up fill).
 */
export default function GarmentFill({ garmentType, cut, sewn, finished, description, width = 200 }: GarmentFillProps) {
    const c = useChartColors();
    const rid = useId();
    const kind = garmentKindFor(garmentType);
    const s = SILHOUETTES[kind];
    const clipId = svgId(rid, 'clip');
    const H = 112;
    const band = (share: number | undefined, fill: string, opacity: number, key: string) => {
        if (share == null) return null;
        const h = clamp01(share) * H;
        return (
            <Box component="rect" key={key} data-testid={`garment-fill-${key}`} data-share={clamp01(share)}
                 x={0} y={H - h} width={120} height={h} fill={fill} fillOpacity={opacity}
                 sx={{ transition: 'y 500ms ease-out, height 500ms ease-out', ...NO_MOTION }} />
        );
    };
    const parts = [
        cut != null ? `cut ${pctText(cut)}` : null,
        sewn != null ? `sewn ${pctText(sewn)}` : null,
        finished != null ? `finished ${pctText(finished)}` : null,
    ].filter(Boolean);
    const spoken = description ?? `${s.label} silhouette. ${parts.length ? parts.join(', ') + ' of the order.' : 'No production figures shown.'}`;
    return (
        <Box data-testid="garment-fill" data-kind={kind} sx={{ width, maxWidth: '100%' }}>
            <Box component="svg" viewBox="0 0 120 112" aria-hidden sx={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
                <defs>
                    <clipPath id={clipId}><path d={s.outline} /></clipPath>
                </defs>
                <g clipPath={`url(#${clipId})`}>
                    <rect x={0} y={0} width={120} height={H} fill={c.surface} />
                    {band(cut, c.series[0], 0.22, 'cut')}
                    {band(sewn, c.series[0], 0.5, 'sewn')}
                    {band(finished, c.series[0], 1, 'finished')}
                </g>
                <path d={s.outline} fill="none" stroke={c.ink} strokeWidth={1.5} strokeLinejoin="round" />
                {s.details && <path d={s.details} fill="none" stroke={c.ink} strokeOpacity={0.55} strokeWidth={1} strokeLinejoin="round" strokeLinecap="round" />}
            </Box>
            <Box component="span" sx={SR_ONLY}>{spoken}</Box>
        </Box>
    );
}
